import { useState } from "react";
import {
  House, Wrench, CheckCircle, Clock, User, Camera, ChatCircle,
  Plus, ArrowLeft, MapPin, Phone, ImagesSquare, Sparkle, PaperPlaneRight,
  CircleDashed, Circle
} from "@phosphor-icons/react";

/**
 * TenantPortalPreview — non-functional design mock so the founder can see the
 * proposed Tenant Self-Service Portal (Phase A: history + status + ETA +
 * add-more-photos). Route: /tenant-portal-preview
 * No backend wiring, no real submissions — pure UI preview.
 */

const MOCK_UNIT = "5346 Chew Ave · Unit 11C";
const MOCK_CREW = "PMR Field Services";
const MOCK_PHONE = "(215) 555-0142";
const MOCK_TENANT_NAME = "Sarah M.";

const MOCK_REQUESTS = [
  {
    id: "r1", ref: "8F2A11", title: "Kitchen sink leak under cabinet",
    category: "Plumbing", urgency: "same_day",
    status: "assigned",
    submitted: "2 hours ago",
    tech: { name: "Mike Ochoa", initials: "MO" },
    eta: "Today · 2:00 PM – 5:00 PM",
    photos: 2,
  },
  {
    id: "r2", ref: "6C40B2", title: "Bedroom heater not warming up",
    category: "HVAC / Heat / AC", urgency: "normal",
    status: "en_route",
    submitted: "Yesterday",
    tech: { name: "Andrew H.", initials: "AH" },
    eta: "Arriving in ~25 min",
    photos: 1,
  },
  {
    id: "r3", ref: "3D9C77", title: "Front door lock sticks in cold",
    category: "Lock / Key / Door", urgency: "normal",
    status: "completed",
    submitted: "3 days ago",
    tech: { name: "Leo Cruz", initials: "LC" },
    eta: "Completed Mon 4:12 PM",
    photos: 3,
    resolution: "Lubed cylinder + adjusted strike plate. Test in cold weather.",
  },
];

const STATUS_META = {
  submitted: { label: "Submitted", color: "bg-slate-600" },
  assigned:  { label: "Assigned",  color: "bg-cyan-700" },
  en_route:  { label: "On the way", color: "bg-amber-500" },
  completed: { label: "Completed", color: "bg-emerald-600" },
};

export default function TenantPortalPreview() {
  const [tab, setTab] = useState("history");
  const [openId, setOpenId] = useState(null);
  const openRequest = MOCK_REQUESTS.find(r => r.id === openId);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-6 px-4">
      <div className="w-full max-w-md">
        {/* Mock disclaimer */}
        <div className="mb-3 p-2 border-2 border-dashed border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800 text-[10px] font-black uppercase tracking-widest text-center">
          ▲ Static Preview — Not wired to backend
        </div>

        {/* Portal header */}
        <div className="bg-[#0f172a] text-white p-5 border-2 border-[#0f172a]">
          <div className="flex items-center gap-2">
            <Sparkle weight="fill" size={18} className="text-cyan-400"/>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-400">
              {MOCK_CREW} · RequestRadar™
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <House weight="fill" size={22}/>
            <div className="font-display font-black text-lg leading-tight">{MOCK_UNIT}</div>
          </div>
          <div className="mt-1 text-[11px] text-slate-300">
            Welcome back, <b className="text-white">{MOCK_TENANT_NAME}</b> · {MOCK_PHONE}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 border-2 border-t-0 border-[#0f172a] bg-white">
          <button
            onClick={() => { setTab("report"); setOpenId(null); }}
            className={`h-12 font-black uppercase text-xs tracking-widest ${tab === "report" ? "bg-cyan-500 text-white" : "text-slate-500"}`}>
            <Plus size={14} className="inline mr-1"/> Report Something
          </button>
          <button
            onClick={() => { setTab("history"); setOpenId(null); }}
            className={`h-12 font-black uppercase text-xs tracking-widest ${tab === "history" ? "bg-cyan-500 text-white" : "text-slate-500"}`}>
            My Requests ({MOCK_REQUESTS.length})
          </button>
        </div>

        {/* Report Something tab (compressed preview) */}
        {tab === "report" && !openId && (
          <div className="border-2 border-t-0 border-[#0f172a] bg-white p-5" data-testid="preview-tab-report">
            <div className="font-display font-black text-lg mb-3">What's going on?</div>
            <input placeholder="e.g. Kitchen sink leaking under cabinet"
              className="w-full h-11 border-2 border-slate-300 px-3 mb-2 text-sm" disabled/>
            <div className="grid grid-cols-3 gap-1 mb-3">
              {["Plumbing", "HVAC", "Electrical", "Appliance", "Leak", "Other"].map(c => (
                <div key={c} className="border-2 border-slate-300 text-center py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  {c}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1 mb-4">
              <div className="border-2 border-cyan-500 bg-cyan-50 text-cyan-800 text-center py-2 text-[10px] font-black uppercase tracking-wider">Normal</div>
              <div className="border-2 border-slate-300 text-center py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">Same Day</div>
              <div className="border-2 border-slate-300 text-center py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">Emergency</div>
            </div>
            <button className="w-full h-12 bg-cyan-500 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] flex items-center justify-center gap-2">
              <Camera weight="fill" size={16}/> Add Photos + Submit
            </button>
            <div className="mt-3 text-[10px] text-slate-500 text-center">
              Same fast intake as today · this preview just adds the "My Requests" tab beside it
            </div>
          </div>
        )}

        {/* My Requests list */}
        {tab === "history" && !openId && (
          <div className="border-2 border-t-0 border-[#0f172a] bg-white p-3 space-y-2" data-testid="preview-tab-history">
            {MOCK_REQUESTS.map(r => {
              const meta = STATUS_META[r.status];
              return (
                <button key={r.id} onClick={() => setOpenId(r.id)}
                  data-testid={`preview-req-${r.id}`}
                  className="w-full text-left p-3 border-2 border-slate-300 hover:border-[#0f172a] bg-white transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-black text-base leading-tight flex-1 min-w-0 truncate">{r.title}</div>
                    <div className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${meta.color}`}>
                      {meta.label}
                    </div>
                  </div>
                  <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5 flex-wrap">
                    <span>#{r.ref}</span>
                    <span>·</span>
                    <span>{r.category}</span>
                    <span>·</span>
                    <span>{r.submitted}</span>
                  </div>
                  {r.status !== "completed" && r.tech && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-slate-50 border border-slate-200">
                      <div className="h-7 w-7 bg-[#0f172a] text-white text-[10px] font-black flex items-center justify-center">
                        {r.tech.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black">{r.tech.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.eta}</div>
                      </div>
                      {r.status === "en_route" && (
                        <div className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-500 text-white animate-pulse">Live</div>
                      )}
                    </div>
                  )}
                  {r.status === "completed" && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                      <CheckCircle weight="fill" size={12}/> {r.eta}
                    </div>
                  )}
                </button>
              );
            })}
            <div className="p-3 text-center text-[10px] text-slate-400 border-2 border-dashed border-slate-200">
              History is auto-recognized by your phone number. No password needed.
            </div>
          </div>
        )}

        {/* Request detail (drill-in) */}
        {openId && openRequest && (
          <div className="border-2 border-t-0 border-[#0f172a] bg-white" data-testid="preview-request-detail">
            <div className="p-3 border-b-2 border-slate-200">
              <button onClick={() => setOpenId(null)}
                className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a] inline-flex items-center gap-1">
                <ArrowLeft size={12}/> Back to My Requests
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2">
                <div className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${STATUS_META[openRequest.status].color}`}>
                  {STATUS_META[openRequest.status].label}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">#{openRequest.ref}</div>
              </div>
              <div className="mt-2 font-display font-black text-xl leading-tight">{openRequest.title}</div>
              <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                <MapPin size={12}/> {MOCK_UNIT} · {openRequest.category}
              </div>

              {/* Timeline */}
              <div className="mt-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Timeline</div>
                <TimelineStep icon={CheckCircle} done label="Submitted" sub={openRequest.submitted}/>
                <TimelineStep icon={CheckCircle} done={openRequest.status !== "submitted"}
                  label={openRequest.tech ? `Assigned to ${openRequest.tech.name}` : "Assigned"}
                  sub={openRequest.tech ? openRequest.eta : "—"}/>
                <TimelineStep icon={openRequest.status === "en_route" ? Circle : (openRequest.status === "completed" ? CheckCircle : CircleDashed)}
                  active={openRequest.status === "en_route"}
                  done={openRequest.status === "completed"}
                  label="On the way"
                  sub={openRequest.status === "en_route" ? "Live ETA · 25 min out" : openRequest.status === "completed" ? "Arrived on time" : ""}/>
                <TimelineStep icon={openRequest.status === "completed" ? CheckCircle : CircleDashed}
                  done={openRequest.status === "completed"}
                  label="Completed"
                  sub={openRequest.status === "completed" ? openRequest.resolution || "Fixed" : ""}
                  last/>
              </div>

              {/* Add more photos + comment (Phase A) */}
              {openRequest.status !== "completed" && (
                <div className="mt-5 space-y-2">
                  <button className="w-full h-11 border-2 border-cyan-500 bg-cyan-50 text-cyan-800 font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2">
                    <ImagesSquare weight="fill" size={14}/> Add more photos to this request
                  </button>
                  <div className="p-3 border-2 border-dashed border-slate-300 bg-slate-50">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                      <ChatCircle size={11}/> Message the office · Phase B
                    </div>
                    <div className="text-[11px] text-slate-500 italic">
                      Reply thread with the office/tech — planned next after Phase A ships
                    </div>
                  </div>
                </div>
              )}

              {/* Contact fallback */}
              <div className="mt-6 p-3 border-t-2 border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
                <Phone size={12}/> Emergency? Call the office at {MOCK_PHONE}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-[10px] text-slate-400">
          Powered by Field Radar · Preview build
        </div>
      </div>
    </div>
  );
}

function TimelineStep({ icon: Icon, label, sub, done = false, active = false, last = false }) {
  const color = done ? "text-emerald-600" : active ? "text-amber-600" : "text-slate-300";
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <Icon weight={done || active ? "fill" : "regular"} size={20} className={color}/>
        {!last && <div className={`w-0.5 flex-1 ${done ? "bg-emerald-600" : "bg-slate-200"} min-h-[24px]`}/>}
      </div>
      <div className="pb-4 flex-1 min-w-0">
        <div className={`text-sm font-black ${done ? "text-slate-900" : active ? "text-amber-800" : "text-slate-400"}`}>{label}</div>
        {sub && <div className={`text-[11px] mt-0.5 ${done ? "text-slate-500" : active ? "text-amber-700" : "text-slate-400"}`}>{sub}</div>}
      </div>
    </div>
  );
}
